import { PropertyStatus } from '@tallo/property';

import { BuildingDto } from '../dtos/building.dto.interface';

/** The Buildings tab's status filter: a building counts by what its units are doing */
export enum BuildingStatusFilter {
  ALL = 'all',
  HAS_LISTED = 'has-listed',
  HAS_READY = 'has-ready',
  HAS_VACANT = 'has-vacant',
  FULLY_OCCUPIED = 'fully-occupied',
}

export const buildingStatusFilterLabels: Record<BuildingStatusFilter, string> = {
  [BuildingStatusFilter.ALL]: 'All',
  [BuildingStatusFilter.HAS_LISTED]: 'Has listed',
  [BuildingStatusFilter.HAS_READY]: 'Has ready',
  [BuildingStatusFilter.HAS_VACANT]: 'Has vacant',
  [BuildingStatusFilter.FULLY_OCCUPIED]: 'Fully occupied',
};

const unitsIn = (building: BuildingDto, status: PropertyStatus) => building.unitsByStatus[status] ?? 0;

/** A unit is vacant until it is rented out, whatever else it is doing */
const vacantUnits = (building: BuildingDto) => building.unitCount - unitsIn(building, PropertyStatus.RENTED_OUT);

export function matchesBuildingStatus(building: BuildingDto, filter: BuildingStatusFilter): boolean {
  switch (filter) {
    case BuildingStatusFilter.HAS_LISTED:
      return unitsIn(building, PropertyStatus.LISTED) > 0;

    case BuildingStatusFilter.HAS_READY:
      return unitsIn(building, PropertyStatus.READY_FOR_LISTING) > 0;

    case BuildingStatusFilter.HAS_VACANT:
      return vacantUnits(building) > 0;

    // An empty building is not occupied; it has nothing to occupy
    case BuildingStatusFilter.FULLY_OCCUPIED:
      return building.unitCount > 0 && vacantUnits(building) === 0;

    default:
      return true;
  }
}

/** How many buildings each filter would show, for the filter's own labels */
export function countByBuildingStatus(buildings: BuildingDto[]): Record<BuildingStatusFilter, number> {
  const counts = {} as Record<BuildingStatusFilter, number>;

  for (const filter of Object.values(BuildingStatusFilter)) {
    counts[filter] = buildings.filter((building) => matchesBuildingStatus(building, filter)).length;
  }

  return counts;
}
